import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { EdaData } from "../../types"; // Assuming EdaResults has correlation and Correlation type exists
import { InfoTooltip } from "../common/InfoTooltip.tsx";

type CorrelationData = EdaData["eda_results"]["correlation"];
type StrongCorrelation = NonNullable<
  CorrelationData["strong_correlations"]
>[number];

interface CorrelationBarChartProps {
  correlationData: CorrelationData | undefined;
}

interface ChartDataPoint {
  name: string; // "FeatureA <-> FeatureB"
  correlation: number;
  color: string;
}

// Function to calculate color based on correlation value
const getColorForCorrelation = (value: number): string => {
  const absoluteValue = Math.abs(value);
  // Map absolute value (0.7 to 1.0) to lightness (e.g., 80% down to 50%)
  // Lower lightness = more intense color
  const lightness = 80 - ((absoluteValue - 0.7) / (1.0 - 0.7)) * 30; // Normalize 0.7-1.0 to 0-1, then scale to 0-30

  if (value > 0) {
    // Positive correlation: Blue scale (HSL: Hue 240)
    return `hsl(240, 70%, ${lightness}%)`;
  } else {
    // Negative correlation: Red scale (HSL: Hue 0)
    return `hsl(0, 70%, ${lightness}%)`;
  }
};

// Helper function to prepare and sort data
const prepareChartData = (
  correlations: StrongCorrelation[] | undefined,
): ChartDataPoint[] => {
  if (!correlations) return [];

  return (
    correlations
      .map((corr) => ({
        name: `${corr.feature1} <-> ${corr.feature2}`,
        correlation: corr.correlation,
        color: getColorForCorrelation(corr.correlation),
      }))
      // Sort by absolute value descending (most correlated first)
      .sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation))
  );
};

export const CorrelationBarChart: React.FC<CorrelationBarChartProps> = ({
  correlationData,
}) => {
  if (!correlationData) {
    return (
      <div className="chart-container card">
        <h3>Korrelationsanalyse</h3>
        <p>Keine Korrelationsdaten verfügbar.</p>
      </div>
    );
  }

  const strongCorrelations = correlationData.strong_correlations;
  const chartData = prepareChartData(strongCorrelations);

  if (!strongCorrelations || strongCorrelations.length === 0) {
    if (correlationData.message) {
      return (
        <div className="chart-container card">
          <h3>Korrelationsanalyse</h3>
          <p>{correlationData.message}</p>
        </div>
      );
    }
    return (
      <div className="chart-container card">
        <h3>Korrelationsanalyse</h3>
        <p>Keine starken Korrelationen (&gt;0.7) gefunden.</p>
      </div>
    );
  }

  return (
    <div className="chart-container card">
      <div className="chart-header-controls">
        <h3>
          Starke Korrelationen
          <InfoTooltip text="Zeigt Paare von numerischen Features/Targets mit Korrelation > |0.7|. Balkenfarbe zeigt Richtung (Blau=Positiv, Rot=Negativ) und Intensität." />
        </h3>
      </div>
      {/* Adjust height based on number of bars */}
      <ResponsiveContainer width="100%" height={30 + chartData.length * 35}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 150, // Adjust left margin for potentially long pair names
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {/* X-Axis shows correlation value from -1 to 1 */}
          <XAxis type="number" domain={[-1, 1]} />
          {/* Y-Axis shows the feature pairs */}
          <YAxis
            dataKey="name"
            type="category"
            width={180} // Increase width for longer names
            interval={0}
            tick={{ fontSize: 11 }} // Slightly smaller font if needed
          />
          <Tooltip formatter={(value: number) => value.toFixed(3)} />
          {/* Reference line at 0 */}
          <ReferenceLine x={0} stroke="var(--text-secondary)" />
          <Bar dataKey="correlation">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
